import { useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";
import { STATUSES } from "../lib/constants";

const STATUS_COLORS = {
  Selesai: "#059669", "Dalam Proses": "#d97706", "Belum Dikerjakan": "#94a3b8", "Perlu Follow Up": "#2563eb",
};

export const DashboardCharts = ({ jobs, onSelectLokasi, onSelectStatus, onSelectTriwulan }) => {
  const locData = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      const k = j.location_id;
      map[k] = map[k] || { id: k, name: j.location_name || "—", total: 0 };
      map[k].total += j.items.length;
    });
    return Object.values(map);
  }, [jobs]);

  const triwulanData = useMemo(
    () => [1, 2, 3, 4].map((t) => ({ triwulan: t, name: `TW ${t}`, total: jobs.filter((j) => j.triwulan === t).length })),
    [jobs]
  );

  const statusData = useMemo(() => {
    const items = jobs.flatMap((j) => j.items);
    return STATUSES.map((s) => ({ name: s, value: items.filter((i) => i.status === s).length })).filter((d) => d.value > 0);
  }, [jobs]);

  const gedungStatusData = useMemo(() => {
    const map = new Map();
    jobs.forEach((j) => j.items.forEach((it) => {
      const name = it.gedung || j.title || "—";
      if (!map.has(name)) map.set(name, { name, Selesai: 0, Proses: 0 });
      if (it.status === "Selesai") map.get(name).Selesai += 1;
      else map.get(name).Proses += 1;
    }));
    return Array.from(map.values());
  }, [jobs]);

  if (!jobs.some((j) => j.items.length > 0)) return null;

  return (
    <>
    <section className="no-print grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="dashboard-charts">
      <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center justify-between">
          <span className="flex items-center gap-1.5"><i className="ph-bold ph-chart-bar text-teal-600" /> Pekerjaan per Lokasi</span>
          {onSelectLokasi && <span className="text-[10px] font-semibold text-slate-400 normal-case">Klik untuk filter</span>}
        </h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={locData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fontWeight: 600, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#cbd5e1" }} interval={0} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "rgba(13,148,136,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Bar dataKey="total" name="Pekerjaan" fill="#0d9488" radius={[6, 6, 0, 0]} maxBarSize={60} isAnimationActive={false}
              cursor={onSelectLokasi ? "pointer" : "default"}
              onClick={(d) => d && onSelectLokasi && onSelectLokasi(d.id)} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm" data-testid="chart-triwulan">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center justify-between">
          <span className="flex items-center gap-1.5"><i className="ph-bold ph-calendar-check text-indigo-600" /> Pekerjaan per Triwulan</span>
          {onSelectTriwulan && <span className="text-[10px] font-semibold text-slate-400 normal-case">Klik untuk filter</span>}
        </h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={triwulanData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 600, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#cbd5e1" }} interval={0} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "rgba(79,70,229,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Bar dataKey="total" name="Pekerjaan" fill="#6366f1" radius={[6, 6, 0, 0]} maxBarSize={50} isAnimationActive={false}
              cursor={onSelectTriwulan ? "pointer" : "default"}
              onClick={(d) => d && onSelectTriwulan && onSelectTriwulan(String(d.triwulan))} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center justify-between">
          <span className="flex items-center gap-1.5"><i className="ph-bold ph-chart-donut text-teal-600" /> Distribusi Status</span>
          <span className="text-[10px] font-semibold text-slate-400 normal-case">Klik untuk filter</span>
        </h3>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none" isAnimationActive={false}
              cursor="pointer" onClick={(d) => d && onSelectStatus && onSelectStatus(d.name)}>
              {statusData.map((d) => <Cell key={d.name} fill={STATUS_COLORS[d.name]} />)}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11, fontWeight: 600 }} iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </section>

    {gedungStatusData.length > 0 && (
      <section className="no-print bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm mb-6" data-testid="chart-gedung-status">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
          <i className="ph-bold ph-buildings text-teal-600" /> Status Item per Gedung
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={gedungStatusData} margin={{ bottom: 70 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fontWeight: 600, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#cbd5e1" }} interval={0} angle={-30} textAnchor="end" height={80} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "rgba(13,148,136,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11, fontWeight: 600 }} iconType="circle" />
            <Bar dataKey="Selesai" stackId="a" fill="#059669" radius={[0, 0, 0, 0]} maxBarSize={44} isAnimationActive={false} />
            <Bar dataKey="Proses" name="Proses / Belum" stackId="a" fill="#d97706" radius={[6, 6, 0, 0]} maxBarSize={44} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </section>
    )}
    </>
  );
};
